import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider, Theme, createTheme } from '@mui/material'
import { StylesProvider as MuiV5StylesProvider } from '@mui/styles'
import { ThemeProvider as BeamThemeProvider, StylesProvider as MuiV4StylesProvider, createTheme as createBeamTheme, themeDefinition as themeDefinitionV4 } from 'beam'
import { themeDefinition } from 'beam-v11'
import { cloneDeep } from 'lodash'
import { ReactElement, useMemo } from 'react'
import { Link, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../root.reducer'
import { AgGridThemeProvider } from './AgGridThemeProvider'
import DEFAULT_FAVICON_URL from './favicon.ico'

declare module '@mui/styles' {
  interface DefaultTheme extends Theme {}
}

let ruleIndex = 0

const getRuleIndex = () => ruleIndex++

const cache = createCache({
  key: 'cone',
  nonce: document.getElementById('main-script')?.nonce,
  prepend: true,
})

export interface ThemeProviderProps {
  children: ReactElement,
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactElement {
  const { t } = useTranslation()
  const themeConfiguration = useAppSelector(state => state.app.theme)
  const { themeV4, themeV5 } = useMemo(() => {
    const themeDefCloneV4 = cloneDeep(themeDefinitionV4)
    const themeDefCloneV5 = cloneDeep(themeDefinition)
    // themeDef.palette.primary.main exists in both versions, but beam exports these values as `ThemeOptions`, which makes those values optional
    // TODO: beam must `export const themeDef = { ... } as const satisfies ThemeOptions` (see https://stackoverflow.com/a/70067918/1170435)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    const themeDefCloneV4Palette = themeDefCloneV4.palette as $TSFIXME
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    const themeDefCloneV5Palette = themeDefCloneV5.palette as $TSFIXME
    if (themeConfiguration && !themeConfiguration.defaultTheme) {
      themeDefCloneV4Palette.primary.main = themeConfiguration.primaryColor
      themeDefCloneV5Palette.primary.main = themeConfiguration.primaryColor
    }
    // All themes now have a navigation text color, I dont see a reason to guard its setting as above
    themeDefCloneV4Palette.navigationTextColor = themeConfiguration?.navigationTextColor ?? '#fff'
    themeDefCloneV5Palette.navigationTextColor = themeConfiguration?.navigationTextColor ?? '#fff'
    return {
      themeV4: createBeamTheme(themeDefCloneV4),
      themeV5: createTheme(themeDefCloneV5),
    }
  }, [themeConfiguration])
  return (
    <>
      <CacheProvider value={cache}>
        <StyledEngineProvider injectFirst>
          {/* The custom class name generators below ensure MUI v4 and v5 class names do not collide */}
          <MuiV4StylesProvider generateClassName={rule => `muiV4-${rule.key}-${getRuleIndex()}`}>
            <MuiV5StylesProvider generateClassName={rule => `muiV5-${rule.key}-${getRuleIndex()}`}>
              {/* Temporarily provide both MUI and Beam themes while migrating to MUI v5 */}
              <BeamThemeProvider theme={themeV4}>
                <MuiThemeProvider theme={themeV5}>
                  <AgGridThemeProvider>
                    {children}
                  </AgGridThemeProvider>
                </MuiThemeProvider>
              </BeamThemeProvider>
            </MuiV5StylesProvider>
          </MuiV4StylesProvider>
        </StyledEngineProvider>
        <Title>
          {themeConfiguration && !themeConfiguration.defaultTheme
            ? themeConfiguration.tabTitle
            : t('appName')}
        </Title>
        <Link
          type="image/x-icon"
          rel="shortcut icon"
          href={
            themeConfiguration && !themeConfiguration.defaultTheme
              ? '/api/rest/theme/image/favicon'
              : DEFAULT_FAVICON_URL
          }
        />
      </CacheProvider>
    </>
  )
}
